// TanStack Query hooks of the hotel screens. Reads keep the previous data on
// screen while refetching (pulsing dot, never a second skeleton); writes never
// retry (409 rule) and invalidate the tape they changed. Keys are
// `['hotel', resource, branch, …]` (hotelKeys) — the property is the branch.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useErp } from '@/app/erp-context';
import { isApiError } from '@/lib/api';
import { baghdadToday } from '@/lib/format';
import { bookApi } from '@/features/booking/public';
import { hotelApi, hotelKeys, type CreateReservationBody, type QuoteQuery } from '../api';
import { useIntentRequest } from './intent';

export function useBranchId(): string | null {
  const { me } = useErp();
  return me.branch?.id ?? null;
}

export function useTapeQuery(from: string, to: string) {
  const branch = useBranchId();
  return useQuery({
    queryKey: hotelKeys.resource('tape', branch, from, to),
    queryFn: () => hotelApi.tape({ from, to }),
    placeholderData: keepPreviousData,
    retry: 1,
  });
}

export function useRoomsToday(enabled = true) {
  const branch = useBranchId();
  return useQuery({
    queryKey: hotelKeys.resource('rooms', branch),
    queryFn: hotelApi.roomsToday,
    enabled,
    placeholderData: keepPreviousData,
    retry: 1,
  });
}

/**
 * The property's business date. A hotel screen usually holds it already (the
 * tape or the today grid in the cache): read it from there; only when nothing
 * does, read the grid once (which the next screen then reuses). A failing read
 * falls back to today in Baghdad so the panel can still default its dates.
 */
export function useBusinessDate(): string | null {
  const qc = useQueryClient();
  const branch = useBranchId() ?? 'main';
  let held: string | null = null;
  for (const resource of ['rooms', 'tape']) {
    for (const [, data] of qc.getQueriesData<{ business_date?: string }>({ queryKey: ['hotel', resource, branch] })) {
      if (data?.business_date) held = data.business_date;
    }
  }
  const grid = useRoomsToday(!held);
  return held ?? grid.data?.business_date ?? (grid.isError ? baghdadToday() : null);
}

export function useRoomPanel(unitId: string | null) {
  const branch = useBranchId();
  return useQuery({
    queryKey: hotelKeys.resource('room', branch, unitId ?? ''),
    queryFn: () => hotelApi.room(unitId as string),
    enabled: !!unitId,
    // a room that is not on the property (404) is an answer, not a blip — no retry
    retry: (n, e) => !(isApiError(e) && e.status === 404) && n < 1,
  });
}

export function useReservation(stayId: string | null | undefined) {
  const branch = useBranchId();
  return useQuery({
    queryKey: hotelKeys.resource('reservation', branch, stayId ?? ''),
    queryFn: () => hotelApi.reservation(stayId as string),
    enabled: !!stayId,
    // a reservation that is not on the property (404) is an answer, not a blip — no retry
    retry: (n, e) => !(isApiError(e) && e.status === 404) && n < 1,
  });
}

/** A stay write moves rooms and nights: every hotel read of the property is stale (tape, grid, panel, reservation). */
export function useInvalidateHotel() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: hotelKeys.all });
}

export function useMoveRoom() {
  const invalidate = useInvalidateHotel();
  const intent = useIntentRequest();
  return useMutation({
    mutationFn: (v: { stayId: string; unitId: string; fromDate: string }) => intent(v, (request_id) => hotelApi.moveRoom(v.stayId, { unit_id: v.unitId, from_date: v.fromDate, request_id })),
    onSuccess: () => invalidate(),
  });
}

export function useExtendStay() {
  const invalidate = useInvalidateHotel();
  const intent = useIntentRequest();
  return useMutation({
    mutationFn: (v: { stayId: string; nights: number }) => intent(v, (requestId) => hotelApi.extend(v.stayId, v.nights, requestId)),
    onSuccess: () => invalidate(),
  });
}

// ------------------------------------------------ 2B-4 «حجز جديد» --

/** GET /hotel/quote for the panel's dates, guests, plan and (when picked) type; off until the dates are a stay. */
export function useQuote(q: QuoteQuery | null) {
  const branch = useBranchId();
  return useQuery({
    queryKey: hotelKeys.resource('quote', branch, q?.from, q?.to, q?.adults, q?.children, q?.plan_id ?? 'rack', q?.unit_type_id ?? '', q?.company_customer_id ?? ''),
    queryFn: () => hotelApi.quote(q as QuoteQuery),
    enabled: !!q,
    placeholderData: keepPreviousData,
    retry: 1,
  });
}

/** The guest search of the panel — the ERP's customer book (`GET /v1/business/customers?q=`). */
export function useGuestSearch(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: ['business', 'customers', term] as const,
    queryFn: () => bookApi.customers(term),
    enabled: term.length > 0,
    staleTime: 30_000,
    retry: 0,
  });
}

/** POST /hotel/reservations — never retried (a refusal stays in the panel with its detail). */
export function useCreateReservation() {
  const invalidate = useInvalidateHotel();
  return useMutation({
    mutationFn: (body: CreateReservationBody) => hotelApi.createReservation(body),
    onSuccess: () => invalidate(),
  });
}

/** The hotel top bar's search: `GET /hotel/reservations?view=all&q=`. */
export function useReservationSearch(q: string) {
  const branch = useBranchId();
  const term = q.trim();
  return useQuery({
    queryKey: hotelKeys.resource('reservations', branch, 'all', term),
    queryFn: () => hotelApi.reservations({ view: 'all', q: term }),
    enabled: term.length > 0,
    placeholderData: keepPreviousData,
    retry: 0,
  });
}
